<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/layout';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '@react-spectrum/layout/package.json';

```jsx import
import {Flex} from '@react-spectrum/layout';
import {View} from '@react-spectrum/view';
```

---
category: Layout
---

# Flex

<p>{docs.exports.Flex.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['Flex']} />

## Introduction

The `Flex` component can be used to layout its children in one dimension with
[flexbox](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Flexible_Box_Layout/Basic_Concepts_of_Flexbox).
Any React Spectrum component can be used as a child, and `Flex` components can be nested to create
more complex layouts.

In addition to the properties widely supported by CSS, React Spectrum also shims the `gap` property, along
with `rowGap` and `columnGap`. These properties make it much easier to build layouts
with consistent space between each item. The gap can be defined with Spectrum [dimension variables](styling.html#dimension-values)
to ensure consistency across applications, and allow the layout to adapt to different devices automatically.
In addition, these values can be autocompleted in many IDEs for convenience.

## Examples

### Vertical stack

This example shows a simple vertical stack, with a gap between each item defined using a Spectrum dimension variable.

```tsx example
<Flex direction="column" width="size-2000" gap="size-100">
  <View backgroundColor="celery-600" height="size-800" />
  <View backgroundColor="blue-600" height="size-800" />
  <View backgroundColor="magenta-600" height="size-800" />
</Flex>
```

### Horizontal stack

This example shows a horizontal stack.

```tsx example
<Flex direction="row" height="size-800" gap="size-100">
  <View backgroundColor="celery-600" width="size-800" />
  <View backgroundColor="blue-600" width="size-800" />
  <View backgroundColor="magenta-600" width="size-800" />
</Flex>
```

### Nesting

This example shows how you can nest flexboxes to create more complicated layouts. It also uses the `flex`
prop on one of the children to expand it to fill the repaining space.

```tsx example
<Flex direction="column" gap="size-100">
  <View backgroundColor="celery-600" height="size-800" />
  <Flex direction="row" height="size-3000" gap="size-100">
    <View backgroundColor="indigo-600" width="size-2000" />
    <View backgroundColor="seafoam-600" flex />
  </Flex>
  <View backgroundColor="magenta-600" height="size-800" />
</Flex>
```

### Wrapping

This example shows how to enable wrapping of items into multiple rows. Resize your browser window to see the
items reflow.

```tsx import
let baseColors = ['celery', 'chartreuse', 'yellow', 'magenta', 'fuchsia', 'purple', 'indigo', 'seafoam', 'red', 'orange', 'green', 'blue'];
let colors = [];
for (let color of baseColors) {
  for (let i = 4; i <= 7; i++) {
    colors.push(`${color}-${i}00`);
  }
}
```

```tsx example
<Flex direction="row" gap="size-100" wrap>
  {colors.map(color =>
    <View key={color} backgroundColor={color} width="size-800" height="size-800" />
  )}
</Flex>
```

### Alignment

The `alignItems` prop can be used to align items along the cross axis. When `direction` is `"column"`, this refers to
horizontal alignment, and when `direction` is `"row"` it refers to vertical alignment.

This example horizontally centers items in a vertical stack.

```tsx example
<Flex direction="column" gap="size-100" alignItems="center">
  <View backgroundColor="celery-600" width="size-800" height="size-800" />
  <View backgroundColor="blue-600" width="size-2000" height="size-800" />
  <View backgroundColor="magenta-600" width="size-800" height="size-800" />
</Flex>
```

**NOTE**: the default value for `alignItems` is `"stretch"` according to the CSS spec. This means that if your items do
not have a size defined in the cross direction, then they will be stretched to fill the height of the container.
This may be undesirable in some cases, e.g. a group of buttons with varying heights. Ensure you set
an `alignItems` value to account for this.

### Justification

The `justifyContent` prop can be used to align items along the main axis. When `direction` is `"column"`, this refers
to vertical alignment, and when `direction` is `"column"` it refers to horizontal alignment.

This example vertically centers the stack of items within the available space defined by a container.

```tsx example
<View height="size-3000" borderWidth="thin" borderColor="dark">
  <Flex direction="column" gap="size-100" justifyContent="center" height="100%">
    <View backgroundColor="celery-600" width="size-800" height="size-800" />
    <View backgroundColor="blue-600" width="size-2000" height="size-800" />
    <View backgroundColor="magenta-600" width="size-800" height="size-800" />
  </Flex>
</View>
```

## Props

<PropTable component={docs.exports.Flex} links={docs.links} />
